import { LANDMARKS, pick } from '../geometry/landmarks.js';
import type { NormalizedLandmark } from '../types/geometry.js';
import { clamp01, roundTo } from '../types/geometry.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';

/**
 * Expression analysis for the «Нейтральное выражение лица» check.
 *
 * Two sources, in order of trust:
 *
 *  1. ARKit-style blendshapes from the model. The smile muscles are the primary
 *     signal; cheek/eye squint only sharpens it, because a mouth smile without
 *     squint is still a smile for a document photo.
 *  2. A geometric fallback that compares the mouth corners with the mouth
 *     centre. The fallback is intentionally modest: without a neutral reference
 *     photo it can only say "the corners are raised", so it always reports a
 *     low confidence and never turns into a hard failure.
 *
 * The check never fails — a relaxed expression is a soft note, not a
 * document requirement — so both sources only ever produce pass / warning /
 * uncertain.
 */

/** Canonical MediaPipe mouth-corner indices (right, left). */
export const MOUTH_CORNER_INDICES = [61, 291] as const;

export type ExpressionSource = 'blendshapes' | 'geometric';

export interface ExpressionAnalysisInput {
  blendshapes?: Record<string, number>;
  landmarks?: readonly NormalizedLandmark[];
  geometry?: FaceGeometryMetrics;
  /** Detector confidence in the face, 0..1. */
  faceConfidence?: number;
}

export interface ExpressionAnalysis {
  /** 0..1, 1 = a full smile. */
  smileScore: number;
  /** 0..1, 1 = the mouth is wide open. */
  mouthOpenScore: number;
  /** 0..1, 1 = the jaw is fully open. */
  jawScore: number;
  /** 0..1, 1 = a neutral face. */
  neutrality: number;
  source: ExpressionSource;
  /** 0..1. */
  confidence: number;
  notes: string[];
}

const channel = (shapes: Record<string, number> | undefined, name: string): number =>
  clamp01(shapes?.[name] ?? 0);

const mean = (...values: number[]): number => {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const EXPRESSION_CHANNELS = [
  'mouthSmileLeft',
  'mouthSmileRight',
  'jawOpen',
  'mouthClose',
  'mouthFunnel',
  'mouthPucker',
] as const;

const hasExpressionBlendshapes = (shapes: Record<string, number> | undefined): boolean =>
  shapes !== undefined && EXPRESSION_CHANNELS.some((name) => typeof shapes[name] === 'number');

const analyseBlendshapes = (
  shapes: Record<string, number>,
  input: ExpressionAnalysisInput,
): ExpressionAnalysis => {
  const smileLeft = channel(shapes, 'mouthSmileLeft');
  const smileRight = channel(shapes, 'mouthSmileRight');
  const squint = mean(
    channel(shapes, 'cheekSquintLeft'),
    channel(shapes, 'cheekSquintRight'),
    channel(shapes, 'eyeSquintLeft'),
    channel(shapes, 'eyeSquintRight'),
  );
  const smileScore = clamp01(((smileLeft + smileRight) / 2) * 0.85 + squint * 0.15);
  const jawScore = channel(shapes, 'jawOpen');
  const mouthOpenScore = clamp01(
    Math.max(
      jawScore,
      channel(shapes, 'mouthFunnel') * 0.6,
      channel(shapes, 'mouthPucker') * 0.6,
    ),
  );
  const browDown = mean(channel(shapes, 'browDownLeft'), channel(shapes, 'browDownRight'));
  const neutrality = clamp01(1 - Math.max(smileScore, jawScore, browDown * 0.6));

  // The blendshape head is near-binary; the only real uncertainty is whether the
  // detector was confident in the face itself.
  const confidence = clamp01(0.6 + 0.35 * clamp01(input.faceConfidence ?? 0.9));
  const notes: string[] = [];
  if (squint > 0.5 && (smileLeft + smileRight) / 2 < 0.25) {
    notes.push('Прищур без улыбки рта: возможно, человек щурится от света, а не улыбается.');
  }

  return {
    smileScore: roundTo(smileScore, 3),
    mouthOpenScore: roundTo(mouthOpenScore, 3),
    jawScore: roundTo(jawScore, 3),
    neutrality: roundTo(neutrality, 3),
    source: 'blendshapes',
    confidence: roundTo(confidence, 3),
    notes,
  };
};

const analyseGeometrically = (input: ExpressionAnalysisInput): ExpressionAnalysis => {
  const geometry = input.geometry;
  const landmarks = input.landmarks ?? geometry?.landmarks ?? [];
  const notes: string[] = [];

  const corners: Array<{ x: number; y: number }> = [];
  for (const index of MOUTH_CORNER_INDICES) {
    const point = pick(landmarks, index);
    if (point) corners.push(point);
  }
  const upperLip = pick(landmarks, LANDMARKS.upperLipInner);
  const lowerLip = pick(landmarks, LANDMARKS.lowerLipInner);
  const center =
    geometry?.mouthCenter ??
    (upperLip && lowerLip
      ? { x: (upperLip.x + lowerLip.x) / 2, y: (upperLip.y + lowerLip.y) / 2 }
      : null);
  const faceHeight =
    geometry && geometry.faceHeight > 0
      ? geometry.faceHeight
      : upperLip && lowerLip
        ? Math.max(1e-3, Math.abs(lowerLip.y - upperLip.y) * 8)
        : 0;

  if (corners.length < 2 || !center || faceHeight <= 0) {
    return {
      smileScore: 0,
      mouthOpenScore: 0,
      jawScore: 0,
      neutrality: 1,
      source: 'geometric',
      confidence: 0.2,
      notes: ['Недостаточно landmarks для геометрической оценки выражения лица.'],
    };
  }

  const cornerY = mean(...corners.map((corner) => corner.y));
  // On screen y grows downwards, so a smile lifts the corners above the centre.
  const lift = (center.y - cornerY) / faceHeight;
  const smileFromLift = clamp01(lift / 0.06);
  const squintFromEyes =
    geometry && geometry.eyeAspectRatio.min > 0
      ? clamp01((0.28 - geometry.eyeAspectRatio.min) / 0.14)
      : 0;
  const smileScore = clamp01(smileFromLift * 0.85 + squintFromEyes * 0.15);

  const lipGapFromLandmarks =
    upperLip && lowerLip ? Math.abs(lowerLip.y - upperLip.y) / faceHeight : 0;
  const jawScore = clamp01(lipGapFromLandmarks / 0.12);
  const mouthOpenScore = jawScore;

  notes.push(
    'Геометрическая оценка без опорного нейтрального кадра: она видит поднятые уголки рта, но не их причину.',
  );

  return {
    smileScore: roundTo(smileScore, 3),
    mouthOpenScore: roundTo(mouthOpenScore, 3),
    jawScore: roundTo(jawScore, 3),
    neutrality: roundTo(clamp01(1 - Math.max(smileScore, jawScore * 0.8)), 3),
    source: 'geometric',
    confidence: 0.5,
    notes,
  };
};

/** Picks the best available source and returns the analysis. */
export const analyseExpression = (input: ExpressionAnalysisInput): ExpressionAnalysis => {
  const shapes = input.blendshapes;
  if (hasExpressionBlendshapes(shapes) && shapes) {
    return analyseBlendshapes(shapes, input);
  }
  return analyseGeometrically(input);
};
